import React, { useEffect, useState } from "react";
import { api, hasAnyRole } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Trash2 } from "lucide-react";

export const LEAVE_TYPES = [
  { key: "annual", label: "Annual Leave", color: "#10B981" },
  { key: "off_day", label: "Off Day", color: "#38BDF8" },
  { key: "emergency", label: "Emergency Leave", color: "#EF4444" },
  { key: "medical", label: "Medical Leave", color: "#F59E0B" },
  { key: "unpaid", label: "Unpaid Leave", color: "#A78BFA" },
];
const leaveMeta = (key) => LEAVE_TYPES.find((t) => t.key === key) || LEAVE_TYPES[0];

export default function CalendarPage() {
  const { user } = useAuth();
  const canManage = hasAnyRole(user, ["admin", "operations"]);
  const [orders, setOrders] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [staff, setStaff] = useState([]);
  const [cursor, setCursor] = useState(new Date());
  const [editing, setEditing] = useState(null); // null | {} (new) | leave

  const load = () => {
    api.get("/orders").then((r) => setOrders(r.data));
    api.get("/leaves").then((r) => setLeaves(r.data)).catch(() => {});
    api.get("/users/staff").then((r) => setStaff(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const startDow = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const eventsOnDay = (d) => orders.filter((o) => {
    const ed = new Date(o.event_date);
    return ed.getFullYear() === d.getFullYear() && ed.getMonth() === d.getMonth() && ed.getDate() === d.getDate();
  });
  const leavesOnDay = (d) => {
    const k = dayKey(d);
    return leaves.filter((l) => l.start_date <= k && k <= (l.end_date || l.start_date));
  };

  const monthName = first.toLocaleString("en-MY", { month: "long", year: "numeric" });

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="calendar-page">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Bookings & Staff Leave</div>
          <h1 className="font-display text-4xl mt-2">{monthName}</h1>
        </div>
        <div className="flex items-center gap-2">
          {canManage && (
            <Button onClick={() => setEditing({})} data-testid="add-leave-btn" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm h-9">
              <Plus className="w-4 h-4 mr-1.5" /> Add Leave
            </Button>
          )}
          <button onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="cal-prev" className="p-2 rounded-sm border border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37]"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => setCursor(new Date())} className="px-3 py-2 text-sm rounded-sm border border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37]">Today</button>
          <button onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="cal-next" className="p-2 rounded-sm border border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37]"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="flex items-center gap-4 mb-4 flex-wrap" data-testid="leave-legend">
        {LEAVE_TYPES.map((t) => (
          <span key={t.key} className="flex items-center gap-1.5 text-[11px] text-zinc-400">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: t.color }} /> {t.label}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-px bg-[#1a1a1a] border border-[#1a1a1a] rounded-sm overflow-hidden">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="bg-[#0a0a0a] py-2 text-center label-eyebrow text-[10px]">{d}</div>
        ))}
        {cells.map((c, i) => {
          if (!c) return <div key={i} className="bg-[#050505] min-h-[110px]" />;
          const evts = eventsOnDay(c);
          const lvs = leavesOnDay(c);
          const isToday = c.toDateString() === new Date().toDateString();
          return (
            <div key={i} className={`bg-[#0a0a0a] min-h-[110px] p-2 space-y-1 ${isToday ? "ring-1 ring-[#D4AF37]" : ""}`}>
              <div className={`text-xs ${isToday ? "text-[#D4AF37] font-medium" : "text-zinc-500"}`}>{c.getDate()}</div>
              {evts.slice(0, 2).map((e) => (
                <Link key={e.id} to={`/orders/${e.id}`} className="block text-[11px] px-1.5 py-0.5 rounded-sm bg-[#1a1a1a] hover:bg-[#D4AF37] hover:text-black truncate transition-colors">
                  {e.customer_name}
                </Link>
              ))}
              {evts.length > 2 && <div className="text-[10px] text-zinc-500">+{evts.length - 2} more</div>}
              {lvs.slice(0, 3).map((l) => {
                const m = leaveMeta(l.leave_type);
                return (
                  <button key={l.id} onClick={() => canManage && setEditing(l)} data-testid={`leave-chip-${l.id}`}
                    title={`${l.staff_name} · ${m.label}${l.note ? ` — ${l.note}` : ""}`}
                    className={`w-full text-left text-[10px] px-1.5 py-0.5 rounded-sm truncate border ${canManage ? "cursor-pointer" : "cursor-default"}`}
                    style={{ color: m.color, borderColor: `${m.color}55`, background: `${m.color}12` }}>
                    {l.staff_name}
                  </button>
                );
              })}
              {lvs.length > 3 && <div className="text-[10px] text-zinc-500">+{lvs.length - 3} on leave</div>}
            </div>
          );
        })}
      </div>

      {editing && (
        <LeaveDialog
          leave={editing}
          staff={staff}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function LeaveDialog({ leave, staff, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({
    staff_id: leave.staff_id || staff[0]?.id || "",
    leave_type: leave.leave_type || "annual",
    start_date: leave.start_date || today,
    end_date: leave.end_date || leave.start_date || today,
    note: leave.note || "",
  });

  const save = async () => {
    if (f.end_date < f.start_date) return toast.error("End date can't be before start date");
    try {
      if (leave.id) {
        await api.patch(`/leaves/${leave.id}`, f);
        toast.success("Leave updated");
      } else {
        await api.post("/leaves", f);
        toast.success("Leave added");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete this leave for ${leave.staff_name}?`)) return;
    try {
      await api.delete(`/leaves/${leave.id}`);
      toast.success("Leave deleted");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{leave.id ? "Edit Staff Leave" : "Add Staff Leave"}</DialogTitle>
          <DialogDescription className="sr-only">Record leave for a staff member.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-zinc-500 uppercase tracking-wider">Staff</label>
            <select value={f.staff_id} onChange={(e) => setF({ ...f, staff_id: e.target.value })} data-testid="leave-staff-select"
              className="w-full bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 h-10 text-sm mt-1">
              {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-zinc-500 uppercase tracking-wider">Leave Type</label>
            <select value={f.leave_type} onChange={(e) => setF({ ...f, leave_type: e.target.value })} data-testid="leave-type-select"
              className="w-full bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 h-10 text-sm mt-1">
              {LEAVE_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-500 uppercase tracking-wider">From</label>
              <Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value, end_date: f.end_date < e.target.value ? e.target.value : f.end_date })} data-testid="leave-start-date" className="bg-[#050505] border-[#2a2a2a] mt-1" />
            </div>
            <div>
              <label className="text-xs text-zinc-500 uppercase tracking-wider">To</label>
              <Input type="date" value={f.end_date} min={f.start_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} data-testid="leave-end-date" className="bg-[#050505] border-[#2a2a2a] mt-1" />
            </div>
          </div>
          <div>
            <label className="text-xs text-zinc-500 uppercase tracking-wider">Note (optional)</label>
            <Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="e.g. Family matters" data-testid="leave-note" className="bg-[#050505] border-[#2a2a2a] mt-1" />
          </div>
          <div className="flex gap-2 pt-2">
            {leave.id && (
              <Button onClick={remove} variant="outline" data-testid="delete-leave-btn" className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-sm">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
            <Button onClick={onClose} variant="outline" className="flex-1 border-[#2a2a2a] rounded-sm">Cancel</Button>
            <Button onClick={save} disabled={!f.staff_id} data-testid="save-leave-btn" className="flex-1 bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm">{leave.id ? "Save Changes" : "Add Leave"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
